import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskProductDetailsBlockConfig } from '@front-platform/content-builder';
import { kioskProductDetailsBlockResolveConfig } from '@front-platform/content-builder';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Injector,
  input,
  OnDestroy,
  Output,
  output,
  Renderer2,
  Signal,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { ProductStoreRefiner } from '@common/refiners/product.refiner';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { VisibilityObserverDirective } from '@core/directives/visibility-observer.directive';
import {
  ICartDraftDeclinableProductItem,
  ICartDraftSimpleProductItem,
} from '@core/models/cart.model';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ProductService } from '@core/services/product/product.service';
import { ProductsService } from '@core/services/products/products.service';
import { TranslateModule } from '@ngx-translate/core';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { KioskDraftCartOrganismComponent } from '@shared/atoms/kiosk/kiosk-draft-cart-composition-organism/kiosk-draft-cart-composition-organism.component';
import { KioskProductDetailsBlockDepsModule } from '@shared/blocks/kiosk/kiosk-product-details-block/kiosk-product-details-block-deps.module';
import { DeclinableProductCart } from '@shared/local-store/cart/cart-draft-declinable-product.store';
import {
  provideRootStepperStore,
  RootStepperStore,
} from '@shared/local-store/stepper/root-stepper.store';
import { Step } from '@shared/local-store/stepper/stepper.type';
import { KioskProductOverviewModalConfig } from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
export interface KioskProductDetailsBlockRuntimeConfig {
  'unavailable_button_background'?: IContentBuilderFieldColor;
  'unavailable_button_text'?: IContentBuilderFieldColor;
  'secondary_color'?: IContentBuilderFieldColor;
  'card_color'?: IContentBuilderFieldColor;
  'composition_background_color'?: IContentBuilderFieldColor;
  'composition_product_id'?: string | undefined;
  'background_image'?: IContentBuilderFieldImage;
}

export type KioskProductDetailsBlockConfigInput = Partial<
  Omit<KioskProductDetailsBlockConfig, keyof KioskProductDetailsBlockRuntimeConfig> &
    KioskProductDetailsBlockRuntimeConfig
>;
export type KioskProductDetailsBlockResolvedConfig = Omit<
  KioskProductDetailsBlockConfig,
  keyof KioskProductDetailsBlockRuntimeConfig
> &
  Partial<KioskProductDetailsBlockRuntimeConfig>;
export interface KioskProductDetailsBlockContract extends ConfigComponentContract<
  KioskProductDetailsBlockConfigInput,
  KioskProductDetailsBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-product-details-block',
  standalone: true,
  imports: [
    TranslateModule,
    ContentBuilderColorPipe,
    ButtonAtomComponent,
    KioskDraftCartOrganismComponent,
    VisibilityObserverDirective,
    KioskProductDetailsBlockDepsModule,
  ],
  providers: [provideRootStepperStore()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative flex h-full flex-col overflow-hidden">
      @if (activeShopId(); as activeShopIdValue) {
        @if (product()?.value(); as product) {
          @if (additionsByGroups(); as additionsByGroupsValue) {
            <h1
              class="text-center font-accent text-4xl font-semibold leading-relaxed kiosk-xl:pb-12 kiosk-xl:text-6xl"
              [style.color]="textColor() | contentBuilderColor"
            >
              {{ product.name }}
              @if (product.description?.length || product.allergens?.length) {
                <i
                  (click)="openProductModal(product)"
                  class="icon icon-c-info-3 pointer-events-auto absolute z-10 content-center pb-3 pl-3 text-center text-2xl font-semibold"
                ></i>
              }
            </h1>

            <app-kiosk-draft-cart-composition-organism
              [product]="product"
              [cartData]="cartData()"
              [config]="{
                primaryButtonBackgroundColor: primaryButtonBackgroundColor(),
                primaryButtonTextColor: primaryButtonTextColor(),
                productBackgroundColor: cardColor(),
                productTextColor: textColor(),
                compositionBackgroundColor: compositionBackgroundColor(),
              }"
            />

            <app-kiosk-stepper-navigation-atom
              [color]="primaryButtonBackgroundColor()"
              [accentColor]="primaryButtonTextColor()"
              [textColor]="textColor()"
              [categoryName]="stepName()"
              [minStepCount]="minStepCount()"
              [maxStepCount]="maxStepCount()"
              [hasNextStep]="hasNextStep()"
              [isNextButtonEnable]="isNextButtonEnable()"
              [hideNextButton]="hideNextButton()"
              [count]="count()"
              [isPreviousButtonEnable]="true"
              (previousStep)="onPreviousStep()"
              (nextStep)="onNextStep()"
            ></app-kiosk-stepper-navigation-atom>
            <div
              class="relative h-full overflow-y-auto"
              #scrollContainer
            >
              <app-kiosk-product-details-organism
                appVisibilityObserver
                (fullVisibilityChange)="onVisibilityChange($event)"
                [threshold]="1"
                [textColor]="textColor()"
                [cardColor]="cardColor()"
                [buttonBackgroundColor]="primaryButtonBackgroundColor()"
                [buttonTextColor]="textColor()"
                [secondaryColor]="secondaryColor()"
                [backgroundColor]="backgroundColor()"
                [backgroundImage]="backgroundImage()"
                [primaryButtonBackgroundColor]="primaryButtonBackgroundColor()"
                [unavailableButtonBackground]="unavailableButtonBackground()"
                [unavailableButtonText]="unavailableButtonText()"
                [primaryButtonTextColor]="primaryButtonTextColor()"
                [secondaryColor]="secondaryColor()"
                [activeShopId]="activeShopIdValue"
                [product]="product"
                [simpleProductAdditionsByGroups]="additionsByGroupsValue"
                (cartData)="cartData.set($event)"
                (additionGroupName)="onAdditionsByGroupChanged($event)"
              />
            </div>
            <div class="mx-auto items-center justify-center gap-6 pt-4">
              @if (!isElementVisible) {
                <div class="flex flex-row gap-6 kiosk-xl:mb-6">
                  <app-button-atom
                    #scrollUpIcon
                    size="inherit"
                    color="primary"
                    [icon]="'icon-chevron-down'"
                    [iconRotation]="'180'"
                    type="kiosk-primary"
                    thickSize="inherit"
                    classContent="w-[9vw] rounded-full "
                    [full]="false"
                    [aspectSquare]="true"
                    [disabled]="disableUpBtn"
                    [overrideBgColor]="primaryButtonBackgroundColor()"
                    [overrideTextColor]="primaryButtonTextColor()"
                    (pointerdown)="startScroll('up')"
                    (pointerup)="stopScroll()"
                    (pointerleave)="stopScroll()"
                  ></app-button-atom>
                  <app-button-atom
                    #scrollDownIcon
                    size="inherit"
                    color="primary"
                    [icon]="'icon-chevron-down'"
                    [iconRotation]="'0'"
                    type="kiosk-primary"
                    thickSize="inherit"
                    classContent="w-[9vw] rounded-full "
                    [full]="false"
                    [disabled]="disableDownBtn"
                    [overrideBgColor]="primaryButtonBackgroundColor()"
                    [overrideTextColor]="primaryButtonTextColor()"
                    [aspectSquare]="true"
                    (pointerdown)="startScroll('down')"
                    (pointerup)="stopScroll()"
                    (pointerleave)="stopScroll()"
                  ></app-button-atom>
                </div>
              }
              @if (!hasNextStep()) {
                <app-button-atom
                  size="XXL"
                  [label]="'declinable-product-stepper.confirm' | translate"
                  color="primary"
                  type="kiosk-primary"
                  [overrideBgColor]="primaryButtonBackgroundColor()"
                  [overrideTextColor]="primaryButtonTextColor()"
                  [iconRotation]="'180'"
                  [iconOnRight]="true"
                  [disabled]="!isNextButtonEnable()"
                  thickSize="M"
                  [full]="true"
                  (click)="onNextStep()"
                  data-testid="kiosk-composition-confirm-button"
                ></app-button-atom>
              }
            </div>
          }
        }
      }
    </div>
  `,
})
export class KioskProductDetailsBlockComponent
  implements OnDestroy, AfterViewInit, KioskProductDetailsBlockContract
{
  readonly config = input<KioskProductDetailsBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskProductDetailsBlockConfig,
    KioskProductDetailsBlockRuntimeConfig
  >(kioskProductDetailsBlockResolveConfig, () => ({ config: this.config() }));
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly modalsService: ModalsService<KioskProductOverviewModalConfig> =
    inject(ModalsService);
  @HostBinding('class') classes = 'h-full overflow-hidden';
  @ViewChild('scrollContainer') scrollContainer: ElementRef | undefined;
  @ViewChild('scrollUpIcon') scrollUpIcon: ElementRef | undefined;
  @ViewChild('scrollDownIcon') scrollDownIcon: ElementRef | undefined;

  protected readonly rootStepperStore = inject(RootStepperStore);

  readonly primaryButtonBackgroundColor = computed(
    () =>
      this.resolvedConfig()[
        'primary_button_background_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly primaryButtonTextColor = computed(
    () =>
      this.resolvedConfig()['primary_button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonBackground = computed(
    () =>
      this.resolvedConfig()[
        'unavailable_button_background'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonText = computed(
    () => this.resolvedConfig()['unavailable_button_text'] as unknown as IContentBuilderFieldColor,
  );
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundImage = computed(
    () =>
      this.resolvedConfig()['background_image'] as unknown as IContentBuilderFieldImage | undefined,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly compositionBackgroundColor = computed(
    () =>
      this.resolvedConfig()['composition_background_color'] as unknown as IContentBuilderFieldColor,
  );
  /** When set, loads this product id even if the route has no `productId` (e.g. fullscreen modal). */
  readonly compositionProductId = computed(
    () => this.resolvedConfig()['composition_product_id'] as unknown as string | undefined,
  );

  readonly selectedStep = output<Step | undefined>();
  readonly backRequest = output<void>();

  private readonly injector = inject(Injector);

  private readonly _destroyerRef = new Subject<boolean>();
  private hasEmittedAddToCart = false;

  @Output() addToCart = new EventEmitter<{
    cartDraft: ICartDraftSimpleProductItem | ICartDraftDeclinableProductItem;
    validity: {
      isValid: boolean;
    };
  }>();

  protected readonly cartData = signal<
    | {
        cart: DeclinableProductCart | ICartDraftSimpleProductItem;
        isValid: boolean;
      }
    | undefined
  >(undefined);

  private readonly shopStoreRefiner = inject(ShopStoreRefiner);

  private readonly cartProducts$ = this.cartRefiner.selectActiveCartProducts;

  private readonly routeProductId$ = this.route.params.pipe(
    switchMap(params => (params['productId'] ? of(params['productId'] as string) : EMPTY)),
  );
  private readonly compositionProductId$ = toObservable(this.compositionProductId, {
    injector: this.injector,
  }).pipe(
    filter((id): id is string => !!id && id.length > 0),
    distinctUntilChanged(),
  );
  private readonly productId$ = merge(this.routeProductId$, this.compositionProductId$).pipe(
    distinctUntilChanged(),
    shareReplay(1),
  );

  private readonly activeShopId$ = this.cartService.getShopActive$().pipe(
    filter(shop => !!shop),
    map(shop => shop?.id as string),
    shareReplay(1),
  );

  protected readonly activeShopId = toSignal(this.activeShopId$);
  protected readonly product = toSignal(
    combineLatest({
      activeShopId: this.activeShopId$,
      productId: this.productId$,
    }).pipe(
      switchMap(({ activeShopId, productId }) => {
        return this.productsService.selectByShopAndProduct$(activeShopId, productId).pipe(take(1));
      }),
      // the returned signal enable to update the product. And each time the productId is changed, the product will be reset by the source
      map(product => ({
        value: signal<Readonly<DoodProductModel> | undefined>(
          product ? { ...product, quantity: 1 } : undefined,
        ),
      })),
    ),
  );

  // Only used by simple products
  protected readonly additionsByGroups = toSignal(
    this.productRefiner.selectAdditionsByGroups.pipe(shareReplay(1), takeUntil(this._destroyerRef)),
    {
      initialValue: undefined,
    },
  );

  protected readonly hideNextButton = computed(() => {
    const hideNextButtonIfTheRequiredQuantityIsOne =
      this.kioskGlobalSettingsService.settings().stepperProductComposition
        .hideNextButtonIfTheRequiredQuantityIsOne;

    if (!hideNextButtonIfTheRequiredQuantityIsOne) {
      return false;
    }
    const currentStepper = this.rootStepperStore.selectors.currentStepper();
    if (!currentStepper) {
      return false;
    }

    const isLastStep = !this.rootStepperStore.selectors.stepperWithTheNextStep()?.step.hasNextStep;
    const autoAddToCart =
      this.kioskGlobalSettingsService.settings().stepperProductComposition.autoAddToCart;
    if (isLastStep && !autoAddToCart) {
      return false;
    }

    const { isDeclinableProductStepperBlocked, isSimpleProductStepperBlocked } =
      this.rootStepperStore.isAutoNextStepBlocked();
    // If a stepper is blocked, it means that the user navigate back
    if (isDeclinableProductStepperBlocked || isSimpleProductStepperBlocked) {
      return false;
    }

    const step = currentStepper.step;

    if (step.minStepCount === 1 && step.maxStepCount === 1) {
      return true;
    }

    return false;
  });

  currentStepper = this.rootStepperStore.selectors.currentStepper;
  stepperWithThePreviousStep = this.rootStepperStore.selectors.stepperWithThePreviousStep;
  stepperWithTheNextStep = this.rootStepperStore.selectors.stepperWithTheNextStep;

  protected readonly isNextButtonEnable: Signal<boolean | undefined> = computed(() => {
    const currentStepper = this.currentStepper?.();
    if (!currentStepper) {
      // todo check if it can be this that blocks the tuna sale menu to add to the cart
      return false;
    }
    return currentStepper.step.isValid;
  });

  protected readonly stepName = toSignal(
    toObservable(this.currentStepper).pipe(
      switchMap(currentStepper => {
        if (!currentStepper) {
          return EMPTY;
        }
        const type = currentStepper?.step.type;
        if (type === 'category') {
          return this.getCategoryName$(currentStepper.step.category.store_id ?? '');
        }
        if (type === 'simpleProductWithAdditionsGroup') {
          if (currentStepper.step.skippable && !currentStepper.step.selectedStepId) {
            return of('');
          }
          return of(currentStepper.step.additionGroup.name);
        }
        return EMPTY;
      }),
    ),
  );

  protected readonly minStepCount: Signal<number | undefined> = computed(() => {
    const currentStepper = this.currentStepper?.();
    if (!currentStepper) {
      return undefined;
    }
    return currentStepper.step.minStepCount;
  });

  protected readonly maxStepCount: Signal<number | undefined> = computed(() => {
    const currentStepper = this.currentStepper?.();
    if (!currentStepper) {
      return undefined;
    }
    return currentStepper.step.maxStepCount;
  });

  protected readonly count: Signal<number | undefined> = computed(() => {
    const currentStepper = this.currentStepper?.();
    if (!currentStepper) {
      return undefined;
    }
    return currentStepper.step.count;
  });

  protected readonly hasNextStep: Signal<boolean> = computed(() => {
    return this.stepperWithTheNextStep()?.step.hasNextStep ?? false;
  });

  isElementVisible: boolean = true;
  scrollInterval: NodeJS.Timeout | undefined = undefined;
  scrollAmount = 100;

  disableUpBtn: boolean = true;
  disableDownBtn: boolean = false;

  protected readonly additionGroupName = signal<string | undefined>(undefined);

  protected readonly foundProductId = computed(() => {
    return this.product()?.value()?.id;
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cartService: CartService,
    private readonly productService: ProductService,
    private readonly productsService: ProductsService,
    private readonly cartRefiner: CartStoreRefiner,
    private readonly productRefiner: ProductStoreRefiner,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService,
  ) {
    effect(
      () => {
        const _productId = this.foundProductId();
        // each time the productId changes, we need to set the product to the store
        untracked(() => {
          const product = this.product()?.value();
          if (!product) {
            return;
          }
          this.productService.setProductToStore(product);
        });
      },
      {
        allowSignalWrites: true,
      },
    );
    effect(
      () => {
        const _productId = this.foundProductId();
        untracked(() => {
          if (!_productId) {
            return;
          }
          this.rootStepperStore.setRootProductId(_productId);
        });
      },
      {
        allowSignalWrites: true,
      },
    );
    effect(() => {
      if (!this.kioskGlobalSettingsService.settings().stepperProductComposition.autoAddToCart) {
        return;
      }
      const currentStepper = this.rootStepperStore.selectors.currentStepper();
      untracked(() => {
        const step = currentStepper?.step;
        if (!step) {
          return;
        }

        if (step.isValid && !step.hasNextStep && !step.canMakeMoreChoices) {
          const nextStepper = this.stepperWithTheNextStep();

          if (!nextStepper?.step.hasNextStep) {
            this._addToCart();
          }
        }
      });
    });
    effect(() => {
      const currentStepper = this.currentStepper();
      if (!currentStepper) {
        return;
      }
      this.selectedStep.emit(currentStepper.step);
    });
  }

  ngOnDestroy() {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  ngAfterViewInit(): void {
    if (this.scrollContainer) {
      this.renderer.listen(this.scrollContainer.nativeElement, 'scroll', (_event: Event) => {
        this.updateScrollIconsOpacity();
      });
      this.updateScrollIconsOpacity();
    }
  }

  protected readonly getCategoryName$ = (categoryId: string) => {
    return this.shopStoreRefiner.selectAllActiveShopStores.pipe(
      map(categories => {
        return categories?.find(category => category.id === categoryId)?.name ?? '';
      }),
    );
  };

  onPreviousStep(): void {
    if (!this.stepperWithThePreviousStep()?.step.hasPreviousStep) {
      this.back();
    } else {
      this.rootStepperStore.previous$.next();
    }
    this.resetScroll();
  }

  private _addToCart() {
    if (this.hasEmittedAddToCart) {
      return;
    }

    const cartData = this.cartData();
    if (cartData?.cart && cartData.isValid) {
      this.hasEmittedAddToCart = true;
      this.addToCart.emit({
        cartDraft: cartData.cart,
        validity: { isValid: cartData.isValid },
      });
    }
  }

  onNextStep(): void {
    if (!this.hasNextStep() && !this.cartData()?.isValid) {
      this.analyticsService.log({
        label: '[DOOD-1026] onNextStep',
        level: 'debug',
        message: {
          isNextButtonEnable: this.isNextButtonEnable(),
          hasNextStep: this.hasNextStep(),
          stepName: this.stepName(),
          currentStepper: this.currentStepper(),
          stepperWithTheNextStep: this.stepperWithTheNextStep(),
          stepperWithThePreviousStep: this.stepperWithThePreviousStep(),
          product: this.product(),
          cartData: this.cartData(),
          additionsByGroups: this.additionsByGroups(),
          additionGroupName: this.additionGroupName(),
        },
      });
    }
    if (!this.isNextButtonEnable()) {
      //#DOOD-836, even if the button is disabled, it may emit the event
      return;
    }
    if (!this.hasNextStep()) {
      this._addToCart();
      return;
    }
    this.rootStepperStore.next$.next();
    this.resetScroll();
  }

  back(): void {
    this.backRequest.emit();
  }

  onVisibilityChange(fullVisibilityChange: boolean): void {
    this.isElementVisible = fullVisibilityChange;
  }

  startScroll(direction: 'up' | 'down'): void {
    this.scrollInterval = setInterval(() => {
      this.scrollTo(direction);
    }, 10);
  }

  stopScroll(): void {
    clearInterval(this.scrollInterval);
  }

  scrollTo(direction: 'up' | 'down'): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.scrollBy({
        top: direction === 'up' ? -this.scrollAmount : this.scrollAmount,
        behavior: 'smooth',
      });
      this.updateScrollIconsOpacity();
    }
  }

  resetScroll(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  updateScrollIconsOpacity(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container && this.scrollUpIcon && this.scrollDownIcon) {
      const scrollTop = container.scrollTop;
      const maxScrollTop = container.scrollHeight - container.clientHeight;
      this.disableUpBtn = scrollTop === 0;
      this.disableDownBtn = scrollTop >= maxScrollTop - 1;
      this.cdr.markForCheck();
    }
  }

  onAdditionsByGroupChanged(newAdditions: string) {
    if (this.additionGroupName() === newAdditions) return;
    this.additionGroupName.set(newAdditions);
  }

  openProductModal(product: DoodProductModel): void {
    this.modalsService.open('kiosk-add-product-overview-modal', undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      colors: {
        background: this.backgroundColor(),
        accent: this.secondaryColor(),
      },
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskProductDetailsBlockComponent, {
  'keyAliases': {},
});
